import { useEffect, useMemo, useRef, useState } from "react";
import {cloneDeep, isNull, isEqual, differenceWith, size, values, isEmpty, keys, isArray} from "lodash";
import { useTranslation, withTranslation } from "react-i18next";
import { Backdrop, CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";

import demandValService from "@plannel/services/data/demand-val-service";
import debug from "@plannel/utils/debug-util";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton, UploadButton } from "@plannel/components/ActionIconButton";
import codeUtil from "@plannel/pages/utils/code-util";
import DateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import WorkbenchUtil from "../WorkbenchUtil";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DpPlanActualService from "@plannel/services/dp/dp-plan-actual-service";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import { AgGridReact } from "ag-grid-react";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";

const DpExcelButton = ({ gridApi, gridRef, responseRef, columnDefs, editableMeasure, params, saveData, filter, editable, isDpPlanActual = false, translationKey = {}, isChannel = false }) => {
  const { t } = useTranslation();
  const { customer, item } = codeUtil;
  const { MF_AMT, MF_QTY, FF_AMT, FF_QTY } = codeUtil.dpMeasure;
  const { DEMAND_PLAN_A, DEMAND_PLAN_Q, BUDGET_PLAN_Q, BUDGET_PLAN_A } = codeUtil.dpPlanActualMeasures;

  const { snackBarMessageColor } = ColorUtil;
  const workbenchThreshold = useRef();
  const dataMap = useRef();
  const headerListRef = useRef([]);
  const excelGridApiRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
  const uploadOpen = Boolean(uploadAnchorEl);
  const importModeRef = useRef("screen");
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [excelGridReady, setExcelGridReady] = useState(false);
  const fileInputRef = useRef();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const EXPORT = "export";
  const EXPORT_ALL = "exportAll";
  const EXPORT_FOR_EDIT = "exportForEdit";
  const EXPORT_ALL_FOR_EDIT = "exportAllForEdit";
  // Advanced Filter 가 적용된 상태에서는 Export All For Edit 을 선택할 수 없다(메뉴 비활성화).
  // 요청 파라미터에 advancedFilters 가 실리는 경우(Workbench.getRequestParams)를 "적용됨"으로 본다.
  const hasAdvancedFilter = !isEmpty(params?.advancedFilters);
  const IMPORT_SCREEN = "import";
  const IMPORT_BULK = "importBulk";
  /* 채널 버전은 measure 구조가 달라(POS_QTY 등) 벌크 Export/Import 왕복 포맷이 안 맞는다 — 채널일 때는 이 두 옵션을 뺀다. */
  const importOptions = isChannel ? {
    [IMPORT_SCREEN]: t(IMPORT_SCREEN)
  } : {
    [IMPORT_SCREEN]: t(IMPORT_SCREEN),
    [IMPORT_BULK]: t(IMPORT_BULK)
  };
  const EXPORT_NAME = isDpPlanActual ? "menuDpPlanVsActual" : "menuDpPractitionerWorkbench";
  const exportOptions = isDpPlanActual ? {
    [EXPORT]: t("exportAsExcel"),
    [EXPORT_ALL]: t("exportAsCsv")
  } : isChannel ? {
    [EXPORT]: t(EXPORT),
    [EXPORT_ALL]: t(EXPORT_ALL),
    [EXPORT_FOR_EDIT]: t(EXPORT_FOR_EDIT)
  } : {
    [EXPORT]: t(EXPORT),
    [EXPORT_ALL]: t(EXPORT_ALL),
    [EXPORT_FOR_EDIT]: t(EXPORT_FOR_EDIT),
    [EXPORT_ALL_FOR_EDIT]: t(EXPORT_ALL_FOR_EDIT)
  };

  const DEFAULT_HEADER = [ "productlevelId", "productlevel", "custlevelId", "custlevel", 
    "itemCdId", "itemCd", "custCdId", "customerCd", "measure" ]

  const setGridColumnDefs = (api, defs) => {
    if (api && defs) {
      api.setGridOption("columnDefs", defs);
    }
  };

  const getExportGridApi = () => {
    return excelGridApiRef.current;
  };

  const getSourceGridApi = () => {
    return gridRef?.current?.api ?? gridApi ?? null;
  };

  const setGridContext = (api) => {
    if (!api?.setGridOption) {
      return;
    }
    const currentContext = api.getGridOption?.("context") ?? {};
    api.setGridOption("context", {
      ...currentContext,
      workbenchThreshold: workbenchThreshold.current,
      dataMap: dataMap.current,
      headerList: headerListRef.current
    });
  };

  const syncHeaderListFromGridApi = () => {
    const sourceGridApi = getSourceGridApi();
    const sourceHeaderList = sourceGridApi?.getGridOption?.("context")?.headerList;
    if (isArray(sourceHeaderList) && sourceHeaderList.length > 0) {
      headerListRef.current = sourceHeaderList;
      return true;
    }
    return false;
  };

  const getDefaultExcelExportParams = (api) => {
    const sourceGridApi = getSourceGridApi();
    return api?.getGridOption?.("defaultExcelExportParams")
      ?? sourceGridApi?.getGridOption?.("defaultExcelExportParams")
      ?? {};
  };

  const excelInit = (rootApi) => {
    excelGridApiRef.current = rootApi.api;
    setExcelGridReady(true);
  };

  const defaultGridSettingMemo = useMemo(() => {
    return DefaultGridSetting({ title: EXPORT_NAME });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!excelGridReady || !excelGridApiRef.current || !columnDefs) {
      return;
    }
    setGridColumnDefs(excelGridApiRef.current, GridUtils.getColumnDefs(columnDefs));
  }, [excelGridReady, columnDefs]);

  const setExcelData = response => {
    const metaData = response.data.resultData;
    const headers = response.data.resultData.headers;
    const formatHeaderList = [];
    const weekFormatHeader = metaData.formatHeaders;
    const userData = JSON.parse(localStorage.getItem("user"));

    for (const index in headers) {
      if (Object.hasOwn(headers, index)) {
        const header = headers[index];
        const temp = new ZDate(header);
        const date = new ZDate(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0);
        let format = DateUtils.headerDateFormatter(date, metaData.timeDimension);
        if (codeUtil.isWeekBucket(metaData.timeDimension)) {
          format = weekFormatHeader[header];
        }
        formatHeaderList.push(format);
      }
    }

    headerListRef.current = formatHeaderList;

    workbenchThreshold.current = response.data.resultData?.workbenchThreshold ?? {};
    if (!isEmpty(response.data.resultData?.workbenchThreshold)) {
      dataMap.current = WorkbenchUtil.makeDataMap({ results: metaData?.pivotdata, timeDimension: metaData?.timeDimension, formatHeaders: formatHeaderList });
    }

    return WorkbenchUtil.setGridDatas(metaData, formatHeaderList, userData);
  };

  const setMeasureNm = obj => {
    let measureNm = obj?.measure?.name ? obj?.measure?.name : obj?.measureCd;
    if (isEqual(measureNm, MF_QTY.name)) {
      measureNm = DEMAND_PLAN_Q.name;
    } else if (isEqual(measureNm, MF_AMT.name)) {
      measureNm = DEMAND_PLAN_A.name;
    } else if (isEqual(measureNm, FF_QTY.name)) {
      measureNm = BUDGET_PLAN_Q.name;
    } else if (isEqual(measureNm, FF_AMT.name)) {
      measureNm = BUDGET_PLAN_A.name;
    }

    return measureNm;
  };

  const setPlanActualGridDatas = (headers, results) => {
    // const metaData = results.results;
    // const timeDimension = filter?.timeDimension ?? MONTH;
    // const formatHeaderList = [];

    workbenchThreshold.current = filter?.workbenchThreshold ?? {};
    headerListRef.current = isArray(headers) ? headers : [];

    const datas = [];
    if (!results?.results) {
      return datas;
    }

    for (const result of results.results) {
      // if (!measures.includes(setMeasureNm(result))) {
      //   continue;
      // }
      const row = { measure: setMeasureNm(result) };
      row.custHrchyNm = result.displayParCustHrchy?.[0] ?? result.customerHrchyNm;
      row.custNm = result.customerNm ?? result.customerHrchyNm;
      row.custCd = result.customerCd ?? result.customerHrchyCd;
      row.itemHrchyNm = result.displayParItemHrchy?.[0] ?? result.itemHrchyNm;
      row.itemNm = result.itemNm ?? result.itemHrchyNm;
      row.itemCd = result.itemCd ?? result.itemHrchyCd;
      row.invClassCd = result.invClassCd;
      row.resultRow = result;
      row.currency = results.currency;

      if (!result?.pivotData) {
        datas.push(row);
        continue;
      }

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        if (typeof result.pivotData[i] !== "number") {
          if (result.pivotData[i]) {
            row[header] = result.pivotData[i];
          } else {
            row[header] = 0;
          }
        } else {
          row[header] = result.pivotData[i];
        }
      }

      datas.push(row);
    }

    const measures = filter?.measures ?? [];
    if (!isEmpty(measures)) {
      return datas.sort((a, b) => measures.indexOf(a.measure) - measures.indexOf(b.measure));
    }

    return datas;
  }

  const setNoteDataForExport = (metaData) => {
    const noteDatas = [];
    if (!metaData) {
      return noteDatas;
    }
    const results = metaData.noteDatas;
    const measureDict = metaData.measureDict;
    for (const result of results) {
      if (!measureDict[result.measureCd]?.name) {
        continue;
      }
      const row = {
        custlevelId: result.customerName,
        productlevelId: result.itemName,
        customerCd: result.customerKey,
        customerName: result.customerName,
        itemCd: result.itemKey,
        itemName: result.itemName,
        measure: GridUtils.gridValueL10N(measureDict[result.measureCd]?.name),
        baseDate: result.date,
        note: result.contentTxt
      };

      noteDatas.push(row);
    }
    return noteDatas;
  };

  const setNoteColumnDef = (columnDef) => {
    const column = [];
    for (const row of columnDef) {
      if (!row.hide) {
        continue;
      }
      if (DEFAULT_HEADER.includes(row?.field) || DEFAULT_HEADER.includes(row?.colId)){
        const copy = cloneDeep(row)
        if (row?.field === "custlevelId" || row?.colId === "custlevel" ) {
          copy.headerName = t("customer");
        }
        if (row?.field === "productlevelId" || row?.colId === "productlevel" ) {
          copy.headerName = t("item");
        }
        copy.rowGroup = false;
        copy.hide = false;
        copy.sort = "asc";
        column.push(copy);
      }
    }
    column.push({ headerName: t("measure"), field: "measure", sort: "asc", hide: false })
    column.push({ headerName: t("notes"), field: "note", sort: "asc", hide: false });
    column.push({ headerName: t("date"), field: "baseDate", sort: "asc", hide: false });

    return column;
  }

  const getExcelExportParams = (isExcelAll = false) => {
    const sort = [];
    let hasItemCode = false;
    if (params.itemLevelCd === item.cd) {
      hasItemCode = columnDefs.filter(item => item.field ==="itemCd").length > 0;
    }

    let hasCustCode = false;
    if (params.custLevelCd === customer.cd) {
      hasCustCode = columnDefs.filter(item => item.field ==="customerCd").length > 0;
    }

    for (const def of columnDefs) {
      if (!def.field) {
        continue;
      }
      if (isExcelAll && def.field === "custlevelId" && !hasCustCode) {
        if (params.custLevelCd === customer.cd) {
          translationKey["customerCd"] = t("customerCd");
          sort.push("customerCd");
        } else {
          translationKey["customerHrchyCd"] = t("customerHrchyCd");
          sort.push("customerHrchyCd");
        }

      }
      if (isExcelAll && def.field === "productlevelId" && !hasItemCode) {
        if (params.itemLevelCd === item.cd) {
          translationKey["itemCd"] = t("itemCd");
          sort.push("itemCd");
        } else {
          translationKey["itemHrchyCd"] = t("itemHrchyCd");
          sort.push("itemHrchyCd");
        }
      }
      translationKey[def.field] = def.headerName;
      sort.push(def.field);
    }

    /*
     * notes 시트 헤더용 — 이 둘은 그리드 컬럼이 아니라서 위 루프로는 안 채워진다.
     * 백엔드(ExcelExportService.createNoteSheet)가 번역본이 없으면 영문 키를 그대로 쓰므로 여기서 넣어준다.
     * (sort 에는 넣지 않는다 — 본문 시트 컬럼이 아니다.)
     */
    // ABC-XYZ Class 컬럼은 헤더 이름이 번역 키("invClassCd") 그대로라서(그리드는 그릴 때 번역) 여기서 번역본을 넣어준다.
    if (sort.includes("invClassCd")) {
      translationKey["invClassCd"] = t("invClassCd");
    }
    translationKey["notes"] = t("notes");
    translationKey["systemField"] = t("systemField");
    translationKey["date"] = t("date");
    // 본문 시트 이름 — 백엔드(ExcelExportService)가 "workbench" 키를 이 번역으로 바꿔 시트 이름으로 쓴다(notes 시트도 위 notes 번역 사용).
    translationKey["workbench"] = t("menuDpPractitionerWorkbench");

    /*
     * 날짜 컬럼 헤더 — 백엔드는 날짜를 "2026-09-28" 로만 쓰므로 화면과 같은 버킷 표기(주: "09/28 W40", 월: "2026-10")를
     * translationKey[날짜] 로 넘긴다. 백엔드(ExcelExportService.dateHeaderName)가 본문 시트 헤더에 이 값을 쓴다.
     * ⚠ Export All For Edit(isExcelAll=true)은 서버 벌크 가져오기가 날짜 헤더를 LocalDate.parse 로 읽으므로 넘기지 않는다
     *   (백엔드도 그 경우는 이 값을 무시하고 "2026-09-28" 그대로 쓴다).
     */
    if (!isExcelAll) {
      const resultData = responseRef?.data?.resultData;
      const rawHeaders = resultData?.headers ?? [];
      const timeDimension = resultData?.timeDimension;
      if (timeDimension) {
        for (const rawHeader of rawHeaders) {
          const label = codeUtil.isWeekBucket(timeDimension)
            ? resultData?.formatHeaders?.[rawHeader]
            : DateUtils.headerDateFormatter(rawHeader, timeDimension);
          if (label) {
            translationKey[rawHeader] = label;
          }
        }
      }
    }

    return sort;
  }

  const allExportForDpPlanActual = (headers, response) => {
    syncHeaderListFromGridApi();
    const excelData = setPlanActualGridDatas(headers, response);
    const excelApi = getExportGridApi();
    if (size(excelData) > 0 && excelApi) {
      if (columnDefs) {
        setGridColumnDefs(excelApi, columnDefs);
      }

      setGridContext(excelApi);
      excelApi.refreshCells();
      GridUtils.setRowData(excelApi, excelData);

      excelApi.exportDataAsExcel({
        skipRowGroups: true, suppressRowOutline: true
      });
    }

    setBackdropOpen(false);
  }

  /*
   * 프론트(ag-grid)에서 직접 xlsx 를 만드는 경로.
   * ⚠ isEdit=true 분기(수정용 3시트 생성)는 EXPORT_FOR_EDIT 가 백엔드 생성으로 바뀌면서 더 이상 호출되지 않는다
   *   — 되돌릴 때를 대비해 남겨둔 것이고, 현재 실제로 쓰이는 건 isEdit=false(EXPORT / EXPORT_ALL) 경로다.
   */
  const allExport = (response, isEdit, isGrid) => {
    syncHeaderListFromGridApi();
    const excelData = setExcelData(response);
    const noteData = setNoteDataForExport(response?.data?.resultData);

    const noteDef = setNoteColumnDef(columnDefs);
    const excelApi = getExportGridApi();

    if (size(excelData) > 0 && excelApi) {
      setGridContext(excelApi);
      excelApi.refreshCells();

      GridUtils.setRowData(excelApi, excelData);
      const preExcelParams = getDefaultExcelExportParams(excelApi);
      let fileName = `plannel_${(preExcelParams.sheetName.replace(/\s/gi, "_")).toLowerCase()}${DateUtils.formatDate(new ZDate(), "yyyy-MM-dd_HH-mm-ss")}`;
      if (isGrid) {
        fileName += ".xlsx";
      } else {
        fileName += `(${t("all")}).xlsx`;
        const cloneColDefs = cloneDeep(columnDefs);
        const showColDefs = cloneColDefs.map((colDef) => {
          if (colDef.colId) {
            colDef.hide = true;
          } else {
            colDef.hide = false;
          }
          colDef.rowGroup = false;
          colDef.sort = null;
          return colDef;
        });
        const filtertedColDefs = showColDefs.filter((defs) => !defs.hide);
        setGridColumnDefs(excelApi, filtertedColDefs);
      }
      const excelExportParams = {
        ...preExcelParams,
        fileName,
        suppressPinnedRowsInExport: false
      };
      GridUtils.setRowData(excelApi, excelData);
      const sheets = [];

      const api = isGrid ? gridRef?.current?.api : excelApi;
      const firstSheet = api?.getSheetDataForExcel({
        sheetName: excelExportParams.sheetName,
      });
      sheets.push(firstSheet);

      setGridColumnDefs(excelApi, noteDef);
      GridUtils.setRowData(excelApi, noteData);

      const secondSheet = excelApi?.getSheetDataForExcel({
        sheetName: t("notes"),
      });
      sheets.push(secondSheet);

      if (size(excelExportParams) > 0) {
        excelApi.exportMultipleSheetsAsExcel({
          ...excelExportParams,
          data: sheets
        });
      }

    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoCurrentData") });
    }
    setBackdropOpen(false);
    setGridColumnDefs(excelApi, columnDefs);
  }

  /*
   * 수정용 내보내기(EXPORT_FOR_EDIT / EXPORT_ALL_FOR_EDIT) 공통 호출부.
   * 두 메뉴 모두 백엔드 /api/demandval/excel-for-edit 를 타고, 조회 범위만 호출부의
   * exportAll(+excel) 플래그로 갈린다 — 응답은 202(비동기 시작) 후 알림으로 다운로드 링크가 온다.
   */
  const retrieveExportAll = async(excelParams) => {
    const {custFilter, itemFilter, customerFilterList, itemFilterList} = excelParams;
    if (custFilter?.length < 1 && itemFilter?.length < 1
      && Object.keys(customerFilterList).length < 1 && Object.keys(itemFilterList).length < 1) {
      if (excelParams.custLevelCd === customer.cd) {
        excelParams.custLevelCd = null;
      }
      if (excelParams.itemLevelCd === item.cd) {
        excelParams.itemLevelCd = null;
      }
    }

    excelParams.translationKey = translationKey;
    // 202 응답(MSG_ExportAsyncStarted)까지 오래 걸리므로, 누르는 즉시 요청 접수 안내를 먼저 보여준다.
    // 이후 응답 결과(시작/없음/중복/오류) 스낵바가 이 안내를 대체한다.
    setSnackInfo({
      open: true,
      severity: "info",
      content: t("MSG_ExportRequested")
    });
    return demandValService.excelAllForEdit(excelParams)
      .then(async response => {
        if (!response) {
          return null;
        }

        if (response.status === 202) {
          const data = await response.json();
          const count = data?.count === 0 ? 1 : data?.count;
          setBackdropOpen(false);
          // 202 를 받은 뒤 2초 후에 시작 메시지를 띄운다 (그 사이에는 위의 접수 안내가 유지된다).
          setTimeout(() => {
            setSnackInfo({
              open: true,
              severity: snackBarMessageColor.success,
              content: t("MSG_ExportAsyncStarted", { N: count })
            });
          }, 2000);
          return null;
        }

        if (response.status === 204) {
          setBackdropOpen(false);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return null;
        }

        if (response.status === 409) {
          setBackdropOpen(false);
          setSnackInfo({
            open: true,
            severity: snackBarMessageColor.inputError,
            content: t("MSG_taskAlreadyRunning")
          });
          return null;
        }

        if (!response.ok) {
          setBackdropOpen(false);
          setSnackInfo({
            open: true,
            severity: snackBarMessageColor.networkError,
            content: t("MSG_NetworkError")
          });
          return null;
        }
      })
      .catch(e => {
        setBackdropOpen(false);
        debug(e);
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.networkError,
          content: e.message
        });
      });
  }

  const handleLoadData = (response, order, versionBucket = null) => {
    const timeBucket = versionBucket;
    if (!response.results) {
      return;
    } else {
      const { headers } = response.results?.[0];
      let sortHeaders = headers;
      const formatHeaderList = [];

      if (codeUtil.isWeekBucket(timeBucket)) {
        for (const sortHeader of sortHeaders) {
          formatHeaderList.push(headers[sortHeader]);
        }
      } else {
        if (isArray(headers)) {
          for (const header of headers) {
            const headerFormat = dateUtils.headerDateFormatter(new ZDate(header), timeBucket);
            formatHeaderList.push(headerFormat);
          }
        }
      }

      const results = cloneDeep(response.results);

      dataMap.current = WorkbenchUtil.makeDataMap({ results, timeDimension: timeBucket, formatHeaders: sortHeaders });

      allExportForDpPlanActual(filter?.formatHeaders ?? formatHeaderList, response);
    }

  };

  const retrievePlanActual = (order = null) => {
    DpPlanActualService.getAllForCSV(params)
      .then(response => {
        if (!isEmpty(response.data)) {
          const versionBucket = params?.timeDimension;
          handleLoadData(response.data, order, versionBucket);
        }
      })
      .catch(e => {
        const message = e.response?.data?.message;
        setSnackInfo({ open: true, severity: message?.type || snackBarMessageColor.networkError, content: t(message?.message) || t("MSG_NetworkError") });
        debug(e);
        setBackdropOpen(false);
      })
  };
  // excel csv code 그대로 가져옴 공통화 필요
  const filterCsvMeasure = (params) => {
    const unusedCsvExportMeasure = CodeUtil.unusedCsvExportMeasure;
    const active = [];
    const translatedMeasure = {};
    if (params.activeMeasure) {
      for (let measure of params.activeMeasure) {
        if (unusedCsvExportMeasure.includes(measure)) {
          continue;
        }
        active.push(measure);
        if (measure === "BF_VS_ACT_AMT") {
          measure = "BF_VS_ACT_A";
        } else if (measure === "BF_VS_ACT_QTY") {
          measure = "BF_VS_ACT_Q";
        } else if (measure === "MF_AMT") {
          measure = "DEMAND_PLAN_A"
        } else if (measure === "MF_QTY") {
          measure = "DEMAND_PLAN_Q"
        } else if (measure === "FF_AMT") {
          measure = "BUDGET_PLAN_A"
        } else if (measure === "FF_QTY") {
           measure = "BUDGET_PLAN_Q"
        }
        translatedMeasure[codeUtil.dpPlanActualMeasures[measure]?.name] = t(codeUtil.dpPlanActualMeasures[measure]?.name);
      }
      translatedMeasure['customerName'] = t("customerName");
      translatedMeasure['customerHierarchy'] = t("customerHierarchy");
      translatedMeasure['customerCd'] = t("customerCd");
      translatedMeasure['itemHrchy'] = t("itemHrchy");
      translatedMeasure['itemName'] = t("itemName");
      translatedMeasure['itemCd'] = t("itemCd");
      translatedMeasure['invClassCd'] = t("invClassCd");
      translatedMeasure['measure'] = t("measure");

      params.activeMeasure = active;
      params.translatedMeasure = translatedMeasure;
    }
    if (params.activeMeasure.length === 0) {
      return null;
    }
    return params;
  }

  /* eslint-disable consistent-return */
  const retrieveCsvData = (csvParams = params) => {
    const filteredParams = filterCsvMeasure(csvParams);
    if (filteredParams === null) {
      setBackdropOpen(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_MEASURE_CSV_EXPORT_NOT_SUPPORTED") });
      return null;
    }
    return DpPlanActualService.getAllForRawCSV(filteredParams)
      .then(async response => {
        if (!response) {
          return null;
        } else {
          if (response.status === 202) {
            const data = await response.json();
            const count = data?.count === 0 ? 1 : data?.count;
            // 비동기 export: 알림으로 다운로드 링크 전달됨 (export-started 이벤트는 service에서 dispatch) content: t("MSG_ExportAsyncStarted", { N: 0 })
            setSnackInfo({
              open: true,
              severity: snackBarMessageColor.success,
              content: t("MSG_ExportAsyncStarted", {N: count})
            });
            return null;
          }
          if (response.status === 204) {
            setBackdropOpen(false);
            setSnackInfo({open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData")});
            return null;
          }
          //동일 작업이 하고 있는데 다른 곳에서 동일 작업을 하면 409 에러가 떨어지는데, 이 경우에는 네트워크 에러로 간주하지 않고 사용자에게 동일 작업 중이라는 메시지를 보여준다.
          if (response.status === 409) {
            setBackdropOpen(false);
            setSnackInfo({
              open: true,
              severity: snackBarMessageColor.inputError,
              content: t("MSG_taskAlreadyRunning")
            });
            return null;
          }
          if (!response.ok) {
            setBackdropOpen(false);
            setSnackInfo({
              open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError")
            });
            return null;
          }
        }
      }).catch(e => {
        setBackdropOpen(false);
        debug(e);
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.networkError,
          content: t("MSG_NetworkError")
        });
      });
  }

  const convertDataToWorkbook = (dataRows) => {
    const arrayData = new Uint8Array(dataRows);
    const arr = [];
    const xlsx = require("xlsx");

    for (let i = 0; i !== arrayData?.length; ++i) {
      arr[i] = String.fromCharCode(arrayData[i]);
    }

    const bstr = arr.join('');
    return xlsx.read(bstr, { type: 'binary' });
  };

  const exportExcel = (_event, option) => {
    //REMOVE BACKDROP OPEN WHEN EXPORT ALL FOR DP PLAN ACTUAL
    if (isEqual(option, EXPORT)) {
      setBackdropOpen(true);
    }
    setAnchorEl(null);
    if (isEqual(option, EXPORT)) {
      if (isDpPlanActual) {
        retrievePlanActual(filter?.order)
      } else {
        if (!isNull(getSourceGridApi())) {
          setBackdropOpen(false);
          allExport(responseRef, false, true);
        } else {
          setBackdropOpen(false);
        }
      }
    } else if (isEqual(option, EXPORT_ALL)) {
      if (size(params) > 0) {
        if (isDpPlanActual) {
          retrieveCsvData();
        } else {
          const sortList = getExcelExportParams(false);
          retrieveExportAll({ excel: true, exportAll: false, excelType: EXPORT_ALL, ...params, sortList, channel: isChannel });
        }
      } else {
        setBackdropOpen(false);
      }
    }
    else if (isEqual(option, EXPORT_FOR_EDIT)) {
      if (size(params) > 0) {
        const sortList = getExcelExportParams(false);
        retrieveExportAll({ excel: false, exportAll: false, excelType: EXPORT_FOR_EDIT, ...params, sortList, channel: isChannel });
      } else {
        setBackdropOpen(false);
      }
    }
    else if (isEqual(option, EXPORT_ALL_FOR_EDIT)) {
      if (size(params) > 0) {
        const sortList = getExcelExportParams(true);
        retrieveExportAll({ excel: true, exportAll: true, excelType: EXPORT_ALL_FOR_EDIT, ...params, sortList, channel: isChannel });
      } else {
        setBackdropOpen(false);
      }
    }
  };

  const populateGrid = (excelData) => {

    const firstSheetName = excelData.SheetNames[0];
    const secondSheetName = excelData.SheetNames[1];
    const worksheet = excelData.Sheets[firstSheetName];
    const valiSheet = excelData.Sheets[secondSheetName];
    const saveTarget = [];
    const refData = setExcelData(responseRef);
    const { timeDimension } = responseRef.data?.resultData;

    const rawHeaders = refData[0]?.resultRow?.headers ?? [];
    let headers;
    if (codeUtil.isWeekBucket(timeDimension)) {
      const { formatHeaders } = responseRef.data?.resultData;
      headers = rawHeaders.map(h => formatHeaders[h]);
    } else {
      headers = rawHeaders.map(h => DateUtils.headerDateFormatter(h, timeDimension));
    }

    /*
     * 엑셀의 날짜 컬럼명 → 그리드가 쓰는 헤더명 별칭표.
     *
     * 파일을 누가 만들었느냐에 따라 날짜 컬럼명이 다르다.
     *   - 백엔드 생성(지금의 "수정용 내보내기") : LocalDate.toString() 그대로라 "2026-01-01"
     *     (서버 벌크 가져오기가 LocalDate.parse 로 읽어야 해서 이 형식을 바꿀 수 없다)
     *   - 예전 프론트 생성 파일               : 버킷 포맷이라 월이면 "2026-01", 분기면 "2026-1Q"
     * 둘 다 같은 버킷을 가리키므로 양쪽 이름을 모두 그리드 헤더명으로 이어준다.
     */
    const headerAlias = {};
    for (let i = 0; i < rawHeaders.length; i++) {
      const gridHeader = headers[i];
      if (!gridHeader) {
        continue;
      }
      headerAlias[gridHeader] = gridHeader;
      headerAlias[String(rawHeaders[i])] = gridHeader;
      headerAlias[DateUtils.formatDate(new ZDate(rawHeaders[i]), "yyyy-MM-dd")] = gridHeader;
    }

    const maxCnt = Number(worksheet["!ref"]?.split(":")[1].replace(/[A-Z]/g, "")) + 1;
    const getImportColumns = (sheet) => {
      const columns = {}
      const sheetData = [];
      for (const [key, value] of Object.entries(sheet)) {
        const keyNum = key.replace(/[^0-9]/g, "");
        const keyChat = key.replace(/[^A-Z]/g, "");
        if (isEqual(keyNum, "1")) {
          columns[keyChat] = value.w;
        }
      }

      // start at the 2nd row - the first row are the headers
      for (let rowIndex = 2; rowIndex < maxCnt; rowIndex++) {
        const row = {};
        for (const column of Object.keys(columns)) {
          const cell = sheet[column + rowIndex];
          /*
           * 표시 문자열(.w)이 아니라 원래 값(.v)을 우선 읽는다.
           * 백엔드가 셀에 표시 포맷(#,##0 등)을 입히므로 .w 는 반올림·천단위 구분자가 섞인 문자열이고,
           * 그걸 원본 pivotData 와 비교하면 고치지도 않은 셀이 "수정됨"으로 잡힌다.
           */
          row[columns[column]] = cell ? (cell.v ?? cell.w ?? null) : null;
        }
        sheetData.push(row);
      }
      return sheetData;
    };

    const rowData = getImportColumns(worksheet);
    const codeData = getImportColumns(valiSheet);

    /*
     * versionId 는 그리드 행에서 BigInt 표기(`${id}n`)로 만들어지는데 백엔드는 숫자만 적는다.
     * 어느 쪽이 만든 파일이든 같게 보도록 끝의 n 을 떼고 비교한다.
     */
    const normalizeVersionId = value => (value === null || value === undefined) ? "" : String(value).replace(/n$/, "");

    const isMatchVersion = isEqual(normalizeVersionId(codeData[0]?.versionId), normalizeVersionId(refData[0]?.versionId));
    const isMatchLevel = Boolean(isEqual(codeData[0]?.custLevelCd ?? customer.cd, refData[0]?.custLevelCd ?? customer.cd)
    && isEqual(codeData[0]?.prodLevelCd ?? item.cd, refData[0]?.prodLevelCd ?? item.cd));

    if (!isMatchVersion || !isMatchLevel) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotMatchData") });
      return;
    }
    for (const ref of refData) {
      for (let rowIdx = 0; rowIdx < codeData.length; rowIdx++) {
        const validRow = codeData[rowIdx];
        const orginalPivotData = ref.resultRow.pivotData ?? new Array(headers.length).fill(null);
        const pivotData = [];

        if (isEqual(validRow.customerCd, ref.customerCd)
          && isEqual(validRow.itemCd, ref.itemCd)
          && isEqual(validRow.measureCd, ref.measureCd)) {

          const row = rowData[rowIdx];
          for (const col in row) {
            const gridHeader = headerAlias[col];
            if (gridHeader) {
              const val = row[col];
              pivotData.push(val);
              ref[gridHeader] = val;
            }
          }

          const makeCompareObj = (arry) => arry.reduce((acc, cur, idx) => {
            const obj = acc;
            /*
             * 엑셀에서 온 값과 원본 pivotData 를 같은 기준의 숫자로 만들어 비교한다.
             * 숫자면 그대로 쓰고, 문자열(천단위 구분자가 섞인 표시값 등)일 때만 숫자가 아닌 글자를 털어낸다.
             * 이때 음수 부호는 남겨야 한다 — 예전처럼 [^0-9.] 로 싹 지우면 -100 이 100 으로 둔갑한다.
             */
            let replacedVal = null;
            if (cur !== null && cur !== undefined && cur !== "") {
              const parsed = typeof cur === "number" ? cur : Number(cur.toString().replace(/[^0-9.-]/g, ''));
              replacedVal = isNaN(parsed) ? null : parsed;
            }
            obj.push({ [idx]: replacedVal });
            return obj
          }, []);

          const saveObj = makeCompareObj(pivotData);
          const orginalObj = makeCompareObj(orginalPivotData);

          const editedArry = differenceWith(saveObj, orginalObj, isEqual);
          const edited = editedArry.reduce((acc, cur) => {
            let obj = acc;
            obj = Object.assign(obj, cur);
            return obj;
          }, {});

          if (size(edited) > 0 && !values(edited).every(e => e === null)
            && editableMeasure.includes(ref.measureCd)) {
            ref.resultRow.edited = edited;
            if (!isEmpty(edited)) {
              saveTarget.push({ ...ref.resultRow, selectedDto: filter.version, versionDto: filter.version });
            }
          }
        }
      }
    }
    if (size(saveTarget) < 1) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSaveData") });
    } else {
      saveData(saveTarget);
    }
    fileInputRef.current.value = "";
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleImportForEdit = (file) => {
    // 📌 백엔드 /demandval/import-excel 호출 (완료를 기다리지 않고 알림으로 결과를 확인)
    const formData = new FormData();
    formData.append('file', file);  // Excel 파일
    formData.append('versionId', params.versionId.toString());

    demandValService.importAll(formData)
      .then(response => {
        if (response?.data?.message) {
          setBackdropOpen(false);
          setSnackInfo({
            open: true,
            severity: snackBarMessageColor.inputError,
            content: t(response?.data?.message?.message)
          });
          return null;
        }
      })
      .catch(e => {
      debug(e);
      setSnackInfo({
        open: true,
        severity: snackBarMessageColor.networkError,
        content: e.message
      });
    });

    setBackdropOpen(false);
    setSnackInfo({
      open: true,
      severity: snackBarMessageColor.success,
      content: t("MSG_TaskCompleteAlert")
    });
    window.dispatchEvent(new CustomEvent('export-started'));
  };

  // import
  const selectFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);

      fileReader.onload = function (evt) {
        if (importModeRef.current === IMPORT_BULK) {
          setBackdropOpen(true);
          handleImportForEdit(file);
        } else {
          const excelData = convertDataToWorkbook(evt.target.result);
          if (responseRef) {
            populateGrid(excelData);
          }
        }
      }
      fileInputRef.current.value = "";
    }
  }

  const handleUploadClick = (event) => {
    setUploadAnchorEl(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const selectImportOption = (_event, option) => {
    setUploadAnchorEl(null);
    importModeRef.current = option;
    importFile();
  };

  const importFile = () => {
    fileInputRef.current.value = "";
    const fileInput = document.querySelector("#btnImportFile");
    fileInput.click();
  };

  return (
    <>
      <DownloadButton 
        aria-label="export"
        id="lock-button"
        aria-haspopup="listbox"
        aria-controls="lock-menu"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick} />
      {
        isDpPlanActual ? null :
        <UploadButton
          aria-label="import"
          id="upload-button"
          aria-haspopup="listbox"
          aria-controls="upload-menu"
          aria-expanded={uploadOpen ? 'true' : undefined}
          onClick={handleUploadClick}
          disabled={!editable} />
      }
      <input ref={fileInputRef} hidden id="btnImportFile" type="file" onChange={selectFile} />
      <div className="ag-theme-balham" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", visibility: "hidden" }}>
        <AgGridReact
          {...defaultGridSettingMemo}
          onGridReady={excelInit}
          suppressAggFuncInHeader
        />
      </div>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdropOpen}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox',
          dense: true
        }}
      >
        {keys(exportOptions).map((option) => {
          const disabledByAdvancedFilter = isEqual(option, EXPORT_ALL_FOR_EDIT) && hasAdvancedFilter;
          const menuItem = (
            <MenuItem
              key={exportOptions[option]}
              onClick={(event) => exportExcel(event, option)}
              disabled={(isEqual(option, EXPORT_FOR_EDIT) && !editable) || disabledByAdvancedFilter}
            >
              {exportOptions[option]}
            </MenuItem>
          );
          // 비활성(disabled) 항목은 마우스 이벤트를 받지 못해 툴팁이 안 뜨므로 span 으로 감싼다.
          // (MenuItem 쪽 onClick 은 disabled 라 실행되지 않는다)
          if (disabledByAdvancedFilter) {
            return (
              <Tooltip key={exportOptions[option]} title={t("MSG_ExportAllForEditDisabledByAdvancedFilter")} placement="left">
                <span>{menuItem}</span>
              </Tooltip>
            );
          }
          // 수정용 내보내기 후 업로드 시, 기존 값을 지우려면 빈 칸이 아니라 0 을 입력해야 한다는 안내
          return isEqual(option, EXPORT_ALL_FOR_EDIT) ? (
            <Tooltip key={exportOptions[option]} title={t("MSG_ExportAllForEditZeroHint")} placement="left">
              {menuItem}
            </Tooltip>
          ) : menuItem;
        })}
      </Menu>
      <Menu
        id="upload-menu"
        anchorEl={uploadAnchorEl}
        open={uploadOpen}
        onClose={handleUploadClose}
        MenuListProps={{
          'aria-labelledby': 'upload-button',
          role: 'listbox',
          dense: true
        }}
      >
        {keys(importOptions).map((option) => (
          option === IMPORT_BULK ? (
            <Tooltip key={importOptions[option]} title={t("MSG_ImportBulkFileHint")} placement="right">
              <MenuItem onClick={(event) => selectImportOption(event, option)}>
                {importOptions[option]}
              </MenuItem>
            </Tooltip>
          ) : (
            <MenuItem
              key={importOptions[option]}
              onClick={(event) => selectImportOption(event, option)}
            >
              {importOptions[option]}
            </MenuItem>
          )
        ))}
      </Menu>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
};

export default withTranslation()(DpExcelButton); 
